import styled from '@emotion/styled';
import React, { useCallback, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import { DateTime } from 'luxon';
import { SectionContentBannerDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { RelayType } from '@diritto/common/content/constants';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { ImageTransformOptions, ImageTransformType } from '@diritto/common/imageKit/constants';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useSlide } from 'ui-hooks/useSlide';
import { getTextLineStyle, hideScrollbar } from 'styles/common';
import { isPC } from 'constants/layout';
import { SectionSlideButton } from 'app-components/home/sections/components/SectionSlideButton';
import { Link } from 'ui-components/common/Link';
import { ContentCover } from 'ui-components/contents/ContentCover';
import { IkImage } from 'ui-components/Imagekit/IkImage';
import { ZIndex } from 'constants/zIndex';

const Height = 88;
const Width = 346;
const VerticalGap = 8;
const HorizontalGap = 8;
const SingleWidth = Width * 2 + HorizontalGap;

export const SectionContentBanner = ({
  section,
  contents,
  trackerExtraInfo,
}: { section: SectionContentBannerDto; contents: Record<number, ContentRelayDto> } & TrackerExtraInfoProps) => {
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  const handleOnClickTracker = useCallback(
    (bannerTitle: string, contentId: number, listItemIndex: number) => {
      tracker.track(TrackingEventType.SelectContentBannerItem, {
        contentId,
        bannerTitle,
        listItemIndex,
        params: { sectionId: section.id, sectionDesign: section.design, sectionTitle: section.title },
        trackerExtraInfo,
      });
    },
    [section.design, section.id, section.title, tracker, trackerExtraInfo],
  );

  const filteredBanners = useMemo(() => {
    const now = DateTime.now();
    return section.banners.filter(
      banner => now >= DateTime.fromISO(`${banner.startAt}`) && now <= DateTime.fromISO(`${banner.endAt}`),
    );
  }, [section.banners]);

  if (filteredBanners.length === 0) {
    return null;
  }

  return (
    <StyledWrapper>
      <SectionSlideButton
        showSlideButton={showSlideButton}
        handleNextSlide={handleNextSlide}
        handlePrevSlide={handlePrevSlide}
      />
      <StyledScrollContainer ref={slideRef} onScroll={handleSlide} rowCount={1}>
        {filteredBanners.map((banner, index) => {
          const content = contents[banner.contentId];
          if (!content || content.relayType !== RelayType.Public) {
            return null;
          }

          return (
            <Link
              key={banner.title}
              href={banner.link}
              onClick={() => handleOnClickTracker(banner.title, banner.contentId, index)}>
              <StyledBannerWrapper width={section.banners.length === 1 ? SingleWidth : Width}>
                <StyledBlurImageWrapper>
                  <IkImage
                    fullWidth
                    transform={{
                      path: content.cover.path,
                      isPrivate: content.cover.isPrivate,
                      transformation: ImageTransformOptions[ImageTransformType.Icon],
                    }}
                  />
                </StyledBlurImageWrapper>
                <StyleMeta>
                  <StyleTitle>{banner.title}</StyleTitle>
                  <StyleDescription>{banner.description}</StyleDescription>
                </StyleMeta>
                <ContentCover content={content} size={48} ratio={1.33} showBadge={false} />
              </StyledBannerWrapper>
            </Link>
          );
        })}
      </StyledScrollContainer>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  position: relative;
`;

const StyledBannerWrapper = styled.div(
  ({ width }: { width: number }) => css`
    position: relative;
    padding: 12px 14px;
    overflow: hidden;

    width: ${width}px;
    height: ${Height}px;

    border-radius: 4px;
    background-color: ${Color.Black};

    display: flex;
    gap: 16px;
    justify-content: space-between;
    align-items: center;
  `,
);

const StyledBlurImageWrapper = styled.div`
  position: absolute;

  width: 120%;
  aspect-ratio: 692/716;
  left: -10%;

  filter: blur(50px);
  transform: translate3d(0, 0, 0);
  opacity: 0.7;
`;

const StyleMeta = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  z-index: ${ZIndex.SectionBannerBody};
`;

const StyleTitle = styled.div`
  color: ${Color.White};
  font-weight: ${FontWeight.Bold};
  ${Typography.XSmall};
  ${getTextLineStyle(1)};
`;

const StyleDescription = styled.div`
  opacity: 0.6;
  color: ${Color.White};
  font-weight: ${FontWeight.Medium};
  ${Typography.XXSmall};
  ${getTextLineStyle(1)};
`;

const StyledScrollContainer = styled.div(
  ({ rowCount = 1 }: { rowCount?: number }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-flow: column wrap;
    align-content: flex-start;
    padding: 0 16px;
    overflow-y: hidden;
    overflow-x: scroll;
    ${hideScrollbar};
    gap: ${VerticalGap}px ${HorizontalGap}px;
    height: ${Height * rowCount + VerticalGap * (rowCount - 1)}px;

    @media (${isPC}) {
      padding: 0;
      margin: 0 15px;
      overflow-x: hidden;
    }
  `,
);
